import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSettingsSpaceScope } from './hooks/useSettingsSpaceScope';
import { useAuth } from '../../contexts/AuthContext';
import { useVenueDashboardData } from './hooks/useVenueDashboardData';
import { useVenueBookings } from './hooks/useVenueBookings';
import { useCalendarSync } from './hooks/useCalendarSync';
import { useSettingsHandlers } from './hooks/useSettingsHandlers';
import { getBusyTimesForDate } from './utils/bookingUtils';
import { useBlockNumberInputWheel } from './utils/numberInputWheel';
import HomeSection, { clearManageBookingsSession } from './components/HomeSection';
import SettingsSection from './components/SettingsSection';
import MessagesSection from './components/MessagesSection';
import FeedbackSection from './components/FeedbackSection';
import DashboardHeader from './components/DashboardHeader';
import DashboardSidebar from './components/DashboardSidebar';
import SettingsNotificationModal from './components/SettingsNotificationModal';
import { isStripePaymentsUiEnabled, clearStripeFeatureOverrides } from '../../utils/stripeFeatureFlag';
import { touchVenueDashboardActivity } from '../../utils/venueDashboardSession';
import './VenueDashboardNew.css';

export const prefetchVenueHomeHeavyChunks = () => {
  import('./components/ManageBookingsView');
  import('./components/BookingsTimeline');
};

const scheduleIdle = (fn) => {
  if (typeof requestIdleCallback === 'function') {
    return requestIdleCallback(fn, { timeout: 2500 });
  }
  return window.setTimeout(fn, 200);
};

const DASHBOARD_SECTION_KEY_PREFIX = 'venue-dashboard-active-section';
const DASHBOARD_SUBSECTION_KEY_PREFIX = 'venue-dashboard-active-subsection';
const ALLOWED_SECTIONS = new Set(['home', 'settings', 'comms', 'feedback']);

const getScopedStorageKey = (prefix, venueId) => `${prefix}:${String(venueId || 'default')}`;

const readPersistedSection = (venueId) => {
  try {
    const persisted = localStorage.getItem(getScopedStorageKey(DASHBOARD_SECTION_KEY_PREFIX, venueId));
    return ALLOWED_SECTIONS.has(persisted) ? persisted : 'home';
  } catch (error) {
    return 'home';
  }
};

const readPersistedSubSection = (venueId) => {
  try {
    return localStorage.getItem(getScopedStorageKey(DASHBOARD_SUBSECTION_KEY_PREFIX, venueId)) || 'account-settings';
  } catch (error) {
    return 'account-settings';
  }
};

const VenueDashboardNew = ({ venueId }) => {
  const dashboardRef = useRef(null);
  useBlockNumberInputWheel(dashboardRef);
  const { currentUser, userProfile } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeSection, setActiveSection] = useState(() => readPersistedSection(venueId));
  const [activeSubSection, setActiveSubSection] = useState(() => readPersistedSubSection(venueId));
  const [selectedDateBusyTimes, setSelectedDateBusyTimes] = useState([]);
  const [notification, setNotification] = useState(null);
  const [homeViewKey, setHomeViewKey] = useState(0);
  const [pendingMessageThread, setPendingMessageThread] = useState(null);

  useEffect(() => {
    setActiveSection(readPersistedSection(venueId));
    setActiveSubSection(readPersistedSubSection(venueId));
    clearStripeFeatureOverrides();
    touchVenueDashboardActivity();
    prefetchVenueHomeHeavyChunks();
  }, [venueId]);

  useEffect(() => {
    touchVenueDashboardActivity();
    const intervalId = window.setInterval(touchVenueDashboardActivity, 5 * 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(getScopedStorageKey(DASHBOARD_SECTION_KEY_PREFIX, venueId), activeSection);
    } catch (error) {
      // no-op if localStorage unavailable
    }
  }, [venueId, activeSection]);

  useEffect(() => {
    try {
      localStorage.setItem(getScopedStorageKey(DASHBOARD_SUBSECTION_KEY_PREFIX, venueId), activeSubSection);
    } catch (error) {
      // no-op if localStorage unavailable
    }
  }, [venueId, activeSubSection]);
  
  // Custom hooks
  const {
    loading,
    dashboardRefreshing,
    refreshDashboardData,
    error,
    venueName,
    operatingHours,
    setOperatingHours,
    holidays,
    setHolidays,
    dynamicPricing,
    setDynamicPricing,
    defaultPrice,
    setDefaultPrice,
    loadedDefaultPrice,
    defaultPerPersonPrice,
    setDefaultPerPersonPrice,
    loadedDefaultPerPersonPrice,
    dynamicPricingPerPerson,
    setDynamicPricingPerPerson,
    venueDetails,
    setVenueDetails,
    addOnItems,
    setAddOnItems,
    otherItems,
    setOtherItems,
    packages,
    setPackages,
    venueSpaces,
    primarySpaceId,
    discountCodes,
    createVenueDiscountCode,
    deleteVenueDiscountCode,
    flatPricing,
    setFlatPricing,
    bookingRules,
    setBookingRules,
    operatingHoursPerPerson,
    setOperatingHoursPerPerson,
    accountSettings,
    setAccountSettings,
    ownerAccountSettings,
    setOwnerAccountSettings,
    busyTimes,
    fetchBusyTimes
  } = useVenueDashboardData(venueId);

  const spaceScopeSetters = useMemo(
    () => ({
      setOperatingHours,
      setOperatingHoursPerPerson,
      setFlatPricing,
      setDynamicPricing,
      setDynamicPricingPerPerson,
      setDefaultPrice,
      setDefaultPerPersonPrice
    }),
    [
      setOperatingHours,
      setOperatingHoursPerPerson,
      setFlatPricing,
      setDynamicPricing,
      setDynamicPricingPerPerson,
      setDefaultPrice,
      setDefaultPerPersonPrice
    ]
  );

  const {
    settingsSpaceId,
    setSettingsSpaceId,
    multiSpace,
    scopeLoading
  } = useSettingsSpaceScope(venueId, venueSpaces, spaceScopeSetters);
  
  const {
    bookings,
    bookingsByDateIndex,
    bookingSummaryCounts,
    loading: bookingsLoading,
    error: bookingsError,
    loadingPast: bookingsLoadingPast,
    hasMorePast: hasMorePastBookings,
    fetchVenueBookings,
    ensureManageBookingsWindow,
    loadMorePastBookings,
    replaceBookingInList,
    updateBookingStatus,
    updateBookingLocal,
    addBookingLocal
  } = useVenueBookings(venueId);

  useEffect(() => {
    if (loading) return undefined;
    const idleId = scheduleIdle(() => prefetchVenueHomeHeavyChunks());
    return () => {
      if (typeof cancelIdleCallback === 'function' && typeof idleId === 'number') {
        cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId);
      }
    };
  }, [loading]);
  
  const {
    syncLoading,
    lastSyncTime,
    syncCalendar
  } = useCalendarSync(venueId, venueDetails, fetchBusyTimes);

  const isOwnerViewingOwnVenue = userProfile?.owned_venues?.some((v) => v.id === venueId) ?? false;
  
  // Settings handlers
  const {
    draftHolidays,
    draftDynamicPricing,
    handleHolidayDelete,
    onNewHolidayChange,
    addAnotherHoliday,
    removeDraftHoliday,
    addAnotherDynamicPricing,
    onDraftPricingChange,
    removeDraftPricing,
    handleDynamicPricingDelete,
    draftDynamicPricingPerPerson,
    addAnotherDynamicPricingPerPerson,
    onDraftPricingPerPersonChange,
    removeDraftPricingPerPerson,
    handleDynamicPricingPerPersonDelete,
    draftFlatPricing,
    addDraftFlatPricing,
    removeDraftFlatPricing,
    handleFlatPricingDelete,
    handleAccountSettingsSave,
    handleSave
  } = useSettingsHandlers(venueId, {
    operatingHours,
    setOperatingHours,
    holidays,
    setHolidays,
    dynamicPricing,
    setDynamicPricing,
    defaultPrice,
    setDefaultPrice,
    loadedDefaultPrice,
    defaultPerPersonPrice,
    setDefaultPerPersonPrice,
    loadedDefaultPerPersonPrice,
    dynamicPricingPerPerson,
    setDynamicPricingPerPerson,
    accountSettings,
    setAccountSettings,
    setOwnerAccountSettings,
    flatPricing,
    setFlatPricing,
    bookingRules,
    setBookingRules,
    operatingHoursPerPerson,
    setOperatingHoursPerPerson,
    venueDetails,
    setVenueDetails,
    primarySpaceId,
    settingsSpaceId,
    multiSpace,
    onNotify: (payload) => setNotification(payload),
    isOwnerViewingOwnVenue,
  });
  
  const venueTimezone = venueDetails?.timezone || venueDetails?.time_zone || 'Europe/London';
  const stripePaymentsEnabled = isStripePaymentsUiEnabled(venueId, venueDetails);

  useEffect(() => {
    if (!stripePaymentsEnabled && activeSubSection === 'payments') {
      setActiveSubSection('pricing-controls');
    }
  }, [stripePaymentsEnabled, activeSubSection]);

  // Update busy times for selected date
  useEffect(() => {
    if (selectedDate && !isNaN(selectedDate.getTime())) {
      if (busyTimes.length > 0) {
        const busyTimesForDate = getBusyTimesForDate(selectedDate, busyTimes, venueTimezone);
        setSelectedDateBusyTimes(busyTimesForDate);
      } else {
        setSelectedDateBusyTimes([]);
      }
    } else {
      setSelectedDateBusyTimes([]);
    }
  }, [selectedDate, busyTimes, venueId, venueTimezone]);

  const handleSectionChange = useCallback((section) => {
    if (section === 'home') {
      clearManageBookingsSession(venueId);
      setActiveSection((current) => {
        if (current === 'home') {
          setHomeViewKey((key) => key + 1);
        }
        return 'home';
      });
      return;
    }
    setActiveSection(section);
  }, [venueId]);

  const handleOpenMessages = useCallback((payload) => {
    setPendingMessageThread(payload || null);
    setActiveSection('comms');
  }, []);
  
  
  
  if (loading) {
    return (
      <div className="venue-dashboard" ref={dashboardRef}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="venue-dashboard" ref={dashboardRef}>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div
      className={`venue-dashboard${activeSection === 'home' ? ' venue-dashboard--home' : ''}${activeSection === 'settings' ? ' venue-dashboard--settings' : ''}${['home', 'settings', 'feedback', 'comms'].includes(activeSection) ? ' venue-dashboard--narrow' : ''}${activeSection === 'feedback' ? ' venue-dashboard--feedback' : ''}${activeSection === 'comms' ? ' venue-dashboard--comms' : ''}`}
      ref={dashboardRef}
    >
      <SettingsNotificationModal
        show={!!notification}
        type={notification?.type}
        title={notification?.title}
        message={notification?.message}
        onClose={() => setNotification(null)}
      />
      <div className="dashboard-layout">
        <DashboardSidebar 
          activeSection={activeSection}
          onSectionChange={handleSectionChange}
          venueId={venueId}
        />
        
        <div className="canvas-container">
          <DashboardHeader 
            venueName={venueName}
            venueImageUrl={venueDetails?.image_url}
            userProfile={userProfile}
            currentUser={currentUser}
            onRefreshDashboard={refreshDashboardData}
            dashboardRefreshing={dashboardRefreshing}
          />
          
          <div className="main-content">
            <HomeSection
              isVisible={activeSection === 'home'}
              venueId={venueId}
                spaceId={primarySpaceId}
                venueSpaces={venueSpaces}
                defaultPrice={defaultPrice}
                selectedDate={selectedDate}
                onDateSelect={setSelectedDate}
                bookings={bookings}
                bookingsByDateIndex={bookingsByDateIndex}
                bookingSummaryCounts={bookingSummaryCounts}
                selectedDateBusyTimes={selectedDateBusyTimes}
                busyTimes={busyTimes}
                bookingsLoading={bookingsLoading}
                bookingsError={bookingsError}
                onRetryBookings={fetchVenueBookings}
                onEnsurePastWindow={ensureManageBookingsWindow}
                onLoadPastBookings={loadMorePastBookings}
                loadingPastBookings={bookingsLoadingPast}
                hasMorePastBookings={hasMorePastBookings}
                onBookingHydrated={replaceBookingInList}
                onConfirmBooking={updateBookingStatus}
                onPatchBookingLocal={updateBookingLocal}
                onAddBookingLocal={addBookingLocal}
                venueDetails={venueDetails}
                syncCalendar={syncCalendar}
                syncLoading={syncLoading}
                lastSyncTime={lastSyncTime}
                operatingHours={operatingHours}
                operatingHoursPerPerson={operatingHoursPerPerson}
                holidays={holidays}
                dynamicPricing={dynamicPricing}
                defaultPerPersonPrice={defaultPerPersonPrice}
                dynamicPricingPerPerson={dynamicPricingPerPerson}
                bookingRules={bookingRules}
                homeViewKey={homeViewKey}
                onOpenMessages={handleOpenMessages}
                onSectionChange={handleSectionChange}
              />
            {activeSection === 'settings' && (
              <SettingsSection
                activeSubSection={activeSubSection}
                onSubSectionChange={setActiveSubSection}
                accountSettings={accountSettings}
                setAccountSettings={setAccountSettings}
                ownerAccountSettings={ownerAccountSettings}
                isOwnerViewingOwnVenue={isOwnerViewingOwnVenue}
                venueId={venueId}
                venueDetails={venueDetails}
                setVenueDetails={setVenueDetails}
                isAdmin={userProfile?.is_admin ?? false}
                addOnItems={addOnItems}
                setAddOnItems={setAddOnItems}
                otherItems={otherItems}
                setOtherItems={setOtherItems}
                packages={packages}
                setPackages={setPackages}
                operatingHours={operatingHours}
                onOperatingHoursChange={setOperatingHours}
                operatingHoursPerPerson={operatingHoursPerPerson}
                onOperatingHoursPerPersonChange={setOperatingHoursPerPerson}
                holidays={holidays}
                onHolidayDelete={handleHolidayDelete}
                draftHolidays={draftHolidays}
                onNewHolidayChange={onNewHolidayChange}
                addAnotherHoliday={addAnotherHoliday}
                removeDraftHoliday={removeDraftHoliday}
                defaultPrice={defaultPrice}
                onDefaultPriceChange={setDefaultPrice}
                defaultPerPersonPrice={defaultPerPersonPrice}
                onDefaultPerPersonPriceChange={setDefaultPerPersonPrice}
                dynamicPricing={dynamicPricing}
                dynamicPricingPerPerson={dynamicPricingPerPerson}
                onPricingPerPersonDelete={handleDynamicPricingPerPersonDelete}
                draftDynamicPricingPerPerson={draftDynamicPricingPerPerson}
                onDraftPricingPerPersonChange={onDraftPricingPerPersonChange}
                addAnotherDynamicPricingPerPerson={addAnotherDynamicPricingPerPerson}
                removeDraftPricingPerPerson={removeDraftPricingPerPerson}
                flatPricing={flatPricing}
                draftFlatPricing={draftFlatPricing}
                onFlatPricingRowChange={(id, updates) => setFlatPricing(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p))}
                onFlatPricingDelete={handleFlatPricingDelete}
                addDraftFlatPricing={addDraftFlatPricing}
                removeDraftFlatPricing={removeDraftFlatPricing}
                bookingRules={bookingRules}
                onBookingRulesChange={setBookingRules}
                onPricingDelete={handleDynamicPricingDelete}
                draftDynamicPricing={draftDynamicPricing}
                onDraftPricingChange={onDraftPricingChange}
                addAnotherDynamicPricing={addAnotherDynamicPricing}
                removeDraftPricing={removeDraftPricing}
                discountCodes={discountCodes}
                onCreateDiscountCode={createVenueDiscountCode}
                onDeleteDiscountCode={deleteVenueDiscountCode}
                onSave={handleSave}
                onAccountSettingsSave={handleAccountSettingsSave}
                onNotify={(payload) => setNotification(payload)}
                onSectionChange={handleSectionChange}
                stripePaymentsEnabled={stripePaymentsEnabled}
                venueSpaces={venueSpaces}
                settingsSpaceId={settingsSpaceId}
                onSettingsSpaceIdChange={setSettingsSpaceId}
                multiSpace={multiSpace}
                scopeLoading={scopeLoading}
                ownerEmail={venueDetails?.contact_email || currentUser?.email || ''}
                syncCalendar={syncCalendar}
                syncLoading={syncLoading}
              />
            )}
            {activeSection === 'comms' && (
              <MessagesSection
                venueId={venueId}
                initialThreadId={pendingMessageThread?.threadId}
                initialBookingId={pendingMessageThread?.bookingId}
                onSectionChange={handleSectionChange}
              />
            )}
            {activeSection === 'feedback' && (
              <FeedbackSection
                venueId={venueId}
                venueDetails={venueDetails}
                bookingRules={bookingRules}
                bookings={bookings}
                onConfirmBooking={updateBookingStatus}
                onPatchBookingLocal={updateBookingLocal}
                onRetryBookings={fetchVenueBookings}
                onOpenMessages={handleOpenMessages}
                onSectionChange={handleSectionChange}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenueDashboardNew;

